import { useCallback, useEffect, useRef, useState } from "react";
import { API_PATHS, type UserProfileResponse } from "@excalidraw-ms/shared";
import { BACKEND_API_BASE } from "../env";

// Increment when new introduction content should be shown to every user again.
export const AI_INTRODUCTION_VERSION = 1;

export interface AiIntroductionOptions {
  accountId: string;
  getToken: () => Promise<string>;
  access: "full" | "local-only" | "checking" | "temporarily-unavailable";
}

function storageKey(accountId: string) {
  return `excalidraw-ms:ai-introduction:${accountId}`;
}

function version(value: unknown): number {
  if (value === undefined || value === null) return 0;
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error("Invalid introduction preference");
  }
  return value;
}

export function useAiIntroduction({ accountId, getToken, access }: AiIntroductionOptions) {
  const [state, setState] = useState({
    accountId,
    ready: false,
    dismissed: 0,
    busy: false,
    error: "",
  });
  const request = useRef(0);
  const writing = useRef(false);
  const mounted = useRef(false);

  const read = useCallback(async () => {
    if (writing.current) return;
    const id = ++request.current;
    if (access === "checking") return;
    if (access === "temporarily-unavailable") {
      setState({
        accountId,
        ready: false,
        dismissed: 0,
        busy: false,
        error: "Your introduction preference is temporarily unavailable.",
      });
      return;
    }
    try {
      let dismissed = 0;
      if (access === "local-only") {
        const stored = localStorage.getItem(storageKey(accountId));
        dismissed = stored === null ? 0 : version(Number(stored));
      } else {
        const token = await getToken();
        if (id !== request.current || !mounted.current) return;
        const response = await fetch(`${BACKEND_API_BASE}${API_PATHS.profile}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Profile read failed");
        const profile: UserProfileResponse = await response.json();
        dismissed = version(profile.preferences.aiIntroductionDismissedVersion);
        if (id !== request.current || !mounted.current) return;
        const stored = localStorage.getItem(storageKey(accountId));
        const browserDismissed = stored === null ? 0 : version(Number(stored));
        if (browserDismissed > dismissed) {
          const merged = await fetch(`${BACKEND_API_BASE}${API_PATHS.profile}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              preferences: { aiIntroductionDismissedVersion: browserDismissed },
            }),
          });
          if (!merged.ok) throw new Error("Profile reconciliation failed");
          const saved: UserProfileResponse = await merged.json();
          dismissed = version(saved.preferences.aiIntroductionDismissedVersion);
          if (dismissed < browserDismissed) throw new Error("Dismissal was not saved");
        }
      }
      if (id === request.current && mounted.current) {
        setState({ accountId, ready: true, dismissed, busy: false, error: "" });
      }
    } catch {
      if (id === request.current && mounted.current) {
        setState({
          accountId,
          ready: false,
          dismissed: 0,
          busy: false,
          error: "Couldn't load your introduction preference. Try again.",
        });
      }
    }
  }, [accountId, access, getToken]);

  useEffect(() => {
    mounted.current = true;
    writing.current = false;
    setState({ accountId, ready: false, dismissed: 0, busy: false, error: "" });
    void read();
    const refresh = () => {
      void read();
    };
    window.addEventListener("focus", refresh);
    const sync = (event: StorageEvent) => {
      if (access === "local-only" && (event.key === storageKey(accountId) || event.key === null)) {
        void read();
      }
    };
    window.addEventListener("storage", sync);
    return () => {
      mounted.current = false;
      request.current += 1;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", sync);
    };
  }, [accountId, access, read]);

  const dismiss = async () => {
    if (writing.current || (access !== "full" && access !== "local-only")) return false;
    writing.current = true;
    const id = ++request.current;
    setState((previous) => ({ ...previous, busy: true, error: "" }));
    try {
      let dismissed = AI_INTRODUCTION_VERSION;
      if (access === "local-only") {
        const stored = localStorage.getItem(storageKey(accountId));
        dismissed = Math.max(dismissed, stored === null ? 0 : version(Number(stored)));
        localStorage.setItem(storageKey(accountId), String(dismissed));
      } else {
        const token = await getToken();
        if (id !== request.current || !mounted.current) return false;
        const response = await fetch(`${BACKEND_API_BASE}${API_PATHS.profile}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ preferences: { aiIntroductionDismissedVersion: dismissed } }),
        });
        if (!response.ok) throw new Error("Profile write failed");
        const profile: UserProfileResponse = await response.json();
        dismissed = version(profile.preferences.aiIntroductionDismissedVersion);
        if (dismissed < AI_INTRODUCTION_VERSION) throw new Error("Dismissal was not saved");
      }
      if (id !== request.current || !mounted.current) return false;
      setState({ accountId, ready: true, dismissed, busy: false, error: "" });
      return true;
    } catch {
      if (id === request.current && mounted.current) {
        setState((previous) => ({
          ...previous,
          busy: false,
          error: "Couldn't save your preference. Try hiding the introduction again.",
        }));
      }
      return false;
    } finally {
      if (id === request.current) writing.current = false;
    }
  };

  const current = state.accountId === accountId;
  return {
    visible: current && state.ready && state.dismissed < AI_INTRODUCTION_VERSION,
    error: current ? state.error : "",
    busy: current && state.busy,
    dismiss,
    retry: read,
  };
}
